<template>
  <div class="public-home">
    <!-- Hero Section -->
    <section id="hero" class="public-home__hero">
      <div class="public-home__hero-photo-wrap">
        <img
          src="https://rafaela-vieira-lashes.s3.us-west-2.amazonaws.com/rafaela-vieira.jpg"
          alt="Rafaela Vieira"
          class="public-home__hero-photo"
          width="240"
          height="240"
          loading="eager"
        />
      </div>
      <h1 class="public-home__hero-title">Rafaela Vieira</h1>
      <p class="public-home__hero-subtitle">Há mais de uma década realçando olhares incríveis</p>
      <div class="public-home__cta-wrap" v-if="false">
        <button type="button" class="btn btn-primary public-home__cta" @click="scrollToBooking">
          Agendar agora
        </button>
      </div>
    </section>

    
    <!-- Google Reviews Section -->
    <section id="reviews" class="public-home__section public-home__reviews">
      <div v-if="googleReviewsLoading" class="public-home__loading" aria-busy="true">
        <i class="material-icons spin">sync</i>
        <span>Carregando avaliações…</span>
      </div>
      <div v-else-if="googleReviewsError" class="public-home__reviews-error">
        <p>{{ googleReviewsError }}</p>
        <a
          v-if="googleMapsLinkUrl"
          :href="googleMapsLinkUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="btn btn-secondary"
        >
          Ver avaliações no Google
        </a>
      </div>
      <div v-else-if="googleReviews && (googleReviews.reviews.length > 0 || googleReviews.rating != null)" class="public-home__reviews-content">
        <!-- Google Rating card: logo left, rating right -->
        <div class="public-home__reviews-header-card">
          <div class="public-home__reviews-header-card-inner">
            <div class="public-home__reviews-google-logo" aria-hidden="true">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            </div>
            <div class="public-home__reviews-header-content">
              <h2 class="public-home__reviews-card-title">Avaliações no Google</h2>
              <div v-if="googleReviews.rating != null" class="public-home__reviews-summary" :aria-label="'Avaliação: ' + googleReviews.rating + ' de 5 estrelas'">
                <span class="public-home__reviews-rating-num">{{ googleReviews.rating.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) }}</span>
                <div class="public-home__reviews-stars">
                  <span v-for="n in 5" :key="n" class="public-home__reviews-star">
                    <i class="material-icons">{{ getStarIcon(n) }}</i>
                  </span>
                </div>
              </div>
              <p v-if="googleReviews.userRatingCount != null" class="public-home__reviews-count">
                <a
                  v-if="googleMapsLinkUrl"
                  :href="googleMapsLinkUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="public-home__reviews-count-link"
                >
                  Leia nossas {{ googleReviews.userRatingCount.toLocaleString('pt-BR') }} avaliações
                </a>
                <span v-else>Leia nossas {{ googleReviews.userRatingCount.toLocaleString('pt-BR') }} avaliações</span>
              </p>
            </div>
          </div>
        </div>
        <a
          v-if="googleMapsLinkUrl && false"
          :href="googleMapsLinkUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="btn btn-primary public-home__reviews-cta-btn"
        >
          Avaliar no Google
        </a>

        <!-- Carousel: full-width cards (screen minus padding) -->
        <div class="public-home__reviews-carousel-wrap">
          <div
            ref="reviewsCarouselRef"
            class="public-home__reviews-carousel"
            @scroll="onReviewsScroll"
          >
            <div
              class="public-home__reviews-track"
              :style="reviewsTrackStyle"
            >
              <article
                v-for="(review, index) in googleReviews.reviews"
                :key="index"
                class="public-home__review-card-wrap"
                :style="reviewsCardWrapStyle"
              >
                <article class="public-home__review-card">
                  <header class="public-home__review-header">
                    <div class="public-home__review-avatar" aria-hidden="true">
                      {{ getReviewInitials(review.authorName) }}
                    </div>
                    <div class="public-home__review-meta">
                      <span class="public-home__review-author">{{ review.authorName }}</span>
                      <span v-if="review.relativeTime" class="public-home__review-time">{{ review.relativeTime }}</span>
                    </div>
                  </header>
                  <p class="public-home__review-text">{{ review.text }}</p>
                  <footer class="public-home__review-footer">
                    <div v-if="review.rating != null" class="public-home__review-stars" aria-hidden="true">
                      <i v-for="n in 5" :key="n" class="material-icons">{{ n > review.rating ? 'star_border' : 'star' }}</i>
                    </div>
                    <div class="public-home__review-google-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                    </div>
                  </footer>
                </article>
              </article>
            </div>
          </div>
          <button
            v-if="googleReviews.reviews.length > 1"
            type="button"
            class="public-home__reviews-carousel-btn public-home__reviews-carousel-btn--prev"
            aria-label="Avaliação anterior"
            :disabled="reviewsCarouselIndex <= 0"
            @click="goToReviewSlide(reviewsCarouselIndex - 1)"
          >
            <i class="material-icons">chevron_left</i>
          </button>
          <button
            v-if="googleReviews.reviews.length > 1"
            type="button"
            class="public-home__reviews-carousel-btn public-home__reviews-carousel-btn--next"
            aria-label="Próxima avaliação"
            :disabled="reviewsCarouselIndex >= googleReviews.reviews.length - 1"
            @click="goToReviewSlide(reviewsCarouselIndex + 1)"
          >
            <i class="material-icons">chevron_right</i>
          </button>
        </div>

        <!-- Pagination dots -->
        <div v-if="googleReviews.reviews.length > 1" class="public-home__reviews-dots" role="tablist" aria-label="Navegação das avaliações">
          <button
            v-for="(_, index) in googleReviews.reviews"
            :key="index"
            type="button"
            class="public-home__reviews-dot"
            :class="{ 'public-home__reviews-dot--active': reviewsCarouselIndex === index }"
            :aria-label="'Avaliação ' + (index + 1)"
            :aria-selected="reviewsCarouselIndex === index"
            role="tab"
            @click="goToReviewSlide(index)"
          />
        </div>
      </div>
      <div v-else-if="googleMapsLinkUrl" class="public-home__reviews-fallback">
        <a
          :href="googleMapsLinkUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="btn btn-primary"
        >
          Ver avaliações no Google
        </a>
      </div>
    </section>


    <!-- Services Section -->
    <section id="services" class="public-home__section public-home__section--services">
      <h2 class="public-home__section-title">Serviços</h2>
      <p class="public-home__section-subtitle">Selecione um serviço pra agendar</p>
      <div v-if="loading" class="public-home__loading" aria-busy="true">
        <i class="material-icons spin">sync</i>
        <span>Carregando…</span>
      </div>
      <div v-else-if="publicServices.length === 0" class="public-home__empty">
        <p>Em breve teremos novidades.</p>
      </div>
      <div v-else>
        <!-- Selected Services Summary -->
        <div v-if="selectedServiceIds.size > 0 && false" class="public-home__selected-summary">
          <div class="public-home__selected-summary-content">
            <div class="public-home__selected-summary-info">
              <i class="material-icons">check_circle</i>
              <span class="public-home__selected-count">{{ selectedServiceIds.size }} serviço{{ selectedServiceIds.size > 1 ? 's' : '' }} selecionado{{ selectedServiceIds.size > 1 ? 's' : '' }}</span>
            </div>
            <button type="button" class="btn btn-primary btn-sm" @click="scrollToBooking">
              Continuar para agendamento
            </button>
          </div>
        </div>

        <!-- Grouped Services -->
        <div class="public-home__service-groups">
          <div
            v-for="(services, groupName) in groupedServices"
            :key="groupName"
            class="public-home__service-group"
          >
            <button
              type="button"
              class="public-home__service-group-title"
              :class="{ 'public-home__service-group-title--expanded': expandedGroups.has(groupName) }"
              @click="toggleGroup(groupName)"
              :aria-expanded="expandedGroups.has(groupName)"
            >
              <span class="public-home__service-group-chevron">
                <i class="material-icons">{{ expandedGroups.has(groupName) ? 'expand_less' : 'expand_more' }}</i>
              </span>
              <span class="public-home__service-group-name">{{ groupName }}</span>
              <span class="public-home__service-group-count">{{ services.length }}</span>
            </button>
            <div
              v-show="expandedGroups.has(groupName)"
              class="public-home__service-grid"
            >
              <div
                v-for="service in services"
                :key="service.id"
                class="public-home__service-card"
                :class="{ 'public-home__service-card--selected': selectedServiceIds.has(service.id) }"
                @click="toggleService(service)"
              >
                <div v-if="service.imageUrl" class="public-home__service-image-wrap">
                  <img
                    :src="service.imageUrl"
                    :alt="service.name"
                    class="public-home__service-image"
                    loading="lazy"
                  />
                </div>
                <div v-else class="public-home__service-image-placeholder">
                  <i class="material-icons">spa</i>
                </div>
                <div class="public-home__service-body">
                  <h4 class="public-home__service-name">{{ service.name }}</h4>
                  <template v-if="service.description">
                    <button
                      type="button"
                      class="public-home__service-more"
                      :aria-expanded="expandedDescriptionIds.has(service.id)"
                      :aria-controls="'service-desc-' + service.id"
                      @click.stop="toggleDescription(service.id)"
                    >
                      {{ expandedDescriptionIds.has(service.id) ? 'Ver menos' : 'Saiba mais' }}
                    </button>
                    <p
                      v-show="expandedDescriptionIds.has(service.id)"
                      :id="'service-desc-' + service.id"
                      class="public-home__service-desc"
                      :class="{ 'public-home__service-desc--expanded': expandedDescriptionIds.has(service.id) }"
                      role="region"
                      aria-label="Descrição do serviço"
                    >
                      {{ service.description }}
                    </p>
                  </template>
                  <div class="public-home__service-footer">
                    <p class="public-home__service-price">
                      {{ formatPrice(service.price) }}
                    </p>
                    <span v-if="selectedServiceIds.has(service.id)" class="public-home__service-selected-badge">
                      <i class="material-icons">check</i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Booking Flow Section -->
    <section id="booking" class="public-home__section public-home__booking">
      <h2 class="public-home__section-title">Agendar</h2>
      <p class="public-home__section-subtitle">Escolha o serviço, o horário e deixe os seus dados</p>

      <!-- Step Indicator -->
      <div v-if="step !== 'success'" class="public-home__step-indicator">
        <div class="public-home__step-indicator-item" :class="{ active: step === 1, completed: step > 1 }">
          <span class="public-home__step-number"><span class="public-home__step-number-inner">1</span></span>
          <span class="public-home__step-label">Serviço</span>
        </div>
        <span class="public-home__step-connector" aria-hidden="true" />
        <div class="public-home__step-indicator-item" :class="{ active: step === 2, completed: step > 2 }">
          <span class="public-home__step-number"><span class="public-home__step-number-inner">2</span></span>
          <span class="public-home__step-label">Horário</span>
        </div>
        <span class="public-home__step-connector" aria-hidden="true" />
        <div class="public-home__step-indicator-item" :class="{ active: step === 3, completed: step === 'success' }">
          <span class="public-home__step-number"><span class="public-home__step-number-inner">3</span></span>
          <span class="public-home__step-label">Dados</span>
        </div>
      </div>

      <!-- Step 1: Select Services -->
      <div v-if="step === 1" class="public-home__booking-step">
        <h3 class="public-home__step-title">Selecione o serviço</h3>
        <p class="public-home__step-desc">Escolha o serviço que deseja agendar.</p>
        <div v-if="selectedServiceIds.size === 0" class="public-home__hint">
          <i class="material-icons">info</i>
          <span>Selecione o serviço acima para continuar com o agendamento.</span>
        </div>
        <div v-else class="public-home__selected-services-chips">
          <div
            v-for="service in selectedServicesList"
            :key="service.id"
            class="public-home__service-chip"
          >
            <span class="public-home__service-chip-name">{{ service.name }}</span>
            <button
              type="button"
              class="public-home__service-chip-remove"
              aria-label="Remover serviço"
              @click.stop="toggleService(service)"
            >
              <i class="material-icons">close</i>
            </button>
          </div>
        </div>
        <div class="public-home__booking-actions">
          <button
            type="button"
            class="btn btn-primary"
            :disabled="selectedServiceIds.size === 0"
            @click="goToStep(2)"
          >
            Continuar
          </button>
        </div>
      </div>

      <!-- Step 2: Choose Time Slot -->
      <div v-else-if="step === 2" class="public-home__booking-step">
        <h3 class="public-home__step-title">Escolha o dia e horário</h3>
        <p class="public-home__step-desc">Selecione uma data e horário disponível.</p>
        <div v-if="selectedService" class="public-home__selected-service-summary">
          <span class="public-home__selected-service-label">Serviço:</span>
          <span class="public-home__selected-service-name">{{ selectedService.name }}</span>
          <!--<span v-if="selectedService.price" class="public-home__selected-service-price">{{ formatPrice(selectedService.price) }}</span>-->
        </div>
        <div class="public-home__date-picker-wrap">
          <label for="selectedDate" class="public-home__date-label">Data</label>
          <input
            id="selectedDate"
            v-model="selectedDate"
            type="date"
            :min="minDate"
            :max="maxDate"
            class="public-home__date-input"
            required
            aria-label="Selecione a data"
          />
        </div>
        <div v-if="slotsLoading" class="public-home__loading">Carregando horários…</div>
        <div v-else-if="selectedDate" class="public-home__slots-wrap">
          <template v-for="(slotsByDay, dateKey) in filteredSlotsByDate" :key="dateKey">
            <div class="public-home__day-group">
              <div class="public-home__slot-list">
                <button
                  v-for="slot in slotsByDay"
                  :key="slot.date"
                  type="button"
                  class="public-home__slot-btn"
                  :class="{ selected: selectedSlot && selectedSlot.date === slot.date }"
                  @click="selectSlot(slot)"
                >
                  {{ formatSlotTime(slot.date) }}
                </button>
              </div>
            </div>
          </template>
          <p v-if="!slotsLoading && (!filteredSlotsByDate[selectedDate] || filteredSlotsByDate[selectedDate].length === 0)" class="public-home__empty">
            Nenhum horário disponível para esta data.
          </p>
        </div>
        <div v-else class="public-home__slots-wrap">
          <p class="public-home__empty">Selecione uma data para ver os horários disponíveis.</p>
        </div>
        <div class="public-home__booking-actions">
          <button type="button" class="btn btn-secondary" @click="goToStep(1)">Voltar</button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="!selectedSlot"
            @click="goToStep(3)"
          >
            Continuar
          </button>
        </div>
      </div>

      <!-- Step 3: Client Info Form -->
      <div v-else-if="step === 3" class="public-home__booking-step">
        <h3 class="public-home__step-title">Seus dados</h3>
        <p class="public-home__step-desc">Preencha para concluir a solicitação.</p>
        <div v-if="selectedService || selectedSlot" class="public-home__booking-summary">
          <div v-if="selectedService" class="public-home__summary-item">
            <span class="public-home__summary-label">Serviço:</span>
            <span class="public-home__summary-value">{{ selectedService.name }}</span>
            <!--<span v-if="selectedService.price" class="public-home__summary-price">{{ formatPrice(selectedService.price) }}</span>-->
          </div>
          <div v-if="selectedSlot" class="public-home__summary-item">
            <span class="public-home__summary-label">Horário:</span>
            <span class="public-home__summary-value">{{ formatDayLabel(toDateKey(selectedSlot.date)) }} às {{ formatSlotTime(selectedSlot.date) }}</span>
          </div>
        </div>
        <form @submit.prevent="submit" class="public-home__booking-form">
          <div class="form-group">
            <label for="clientName">Nome</label>
            <input
              id="clientName"
              v-model="clientName"
              type="text"
              placeholder="Seu nome"
              aria-invalid="!!nameError"
            />
            <p v-if="nameError" class="field-error" role="alert">{{ nameError }}</p>
          </div>
          <div class="form-group">
            <label for="clientPhone">Telefone</label>
            <PhoneInput
              id="clientPhone"
              v-model="clientPhone"
              aria-label="Telefone"
              :invalid="!!phoneError"
              aria-describedby="clientPhone-error"
            />
            <p v-if="phoneError" id="clientPhone-error" class="field-error" role="alert">{{ phoneError }}</p>
          </div>
          <div class="public-home__booking-actions">
            <button type="button" class="btn btn-secondary" @click="goToStep(2)">Voltar</button>
            <button type="submit" class="btn btn-primary" :disabled="submitLoading">
              {{ submitLoading ? 'Enviando…' : 'Enviar' }}
            </button>
          </div>
        </form>
      </div>

      <!-- Success State -->
      <div v-else-if="step === 'success'" class="public-home__booking-success">
        <i class="material-icons public-home__success-icon">check_circle</i>
        <h3 class="public-home__success-title">Solicitação enviada</h3>
        <p class="public-home__success-text">
          Entraremos em contato para confirmar o seu horário.
        </p>
        <button type="button" class="btn btn-primary" @click="resetBooking">
          Fazer nova solicitação
        </button>
      </div>
    </section>

    <!-- About Section -->
    <section id="about" class="public-home__section">
      <h2 class="public-home__section-title">Sobre</h2>
      <div class="public-home__about-content">
        <p class="public-home__about-text">
          Especializada em extensão de cílios e cuidados com a beleza, oferecemos tratamentos personalizados 
          para realçar a sua beleza natural. Com anos de experiência e dedicação, garantimos resultados 
          excepcionais e um atendimento de qualidade.
        </p>
      </div>
    </section>

    <!-- Contact/Address Section -->
    <section id="contact" class="public-home__section public-home__contact">
      <h2 class="public-home__section-title">Contato</h2>
      <div class="public-home__contact-grid">
        <div class="public-home__contact-item">
          <i class="material-icons public-home__contact-icon">location_on</i>
          <div class="public-home__contact-info">
            <h3 class="public-home__contact-label">Endereço</h3>
            <p class="public-home__contact-value" v-html="addressHtml || 'A definir'"></p>
          </div>
        </div>
        <div class="public-home__contact-item">
          <i class="material-icons public-home__contact-icon">phone</i>
          <div class="public-home__contact-info">
            <h3 class="public-home__contact-label">Telefone</h3>
            <p class="public-home__contact-value">
              <a v-if="contactInfo.phone" :href="'tel:' + contactInfo.phone.replace(/\D/g, '')" class="public-home__contact-link">
                {{ contactInfo.phone }}
              </a>
              <span v-else>A definir</span>
            </p>
          </div>
        </div>
        <div class="public-home__contact-item">
          <i class="material-icons public-home__contact-icon">schedule</i>
          <div class="public-home__contact-info">
            <h3 class="public-home__contact-label">Horário de funcionamento</h3>
            <p class="public-home__contact-value" v-html="businessHoursHtml || 'A definir'"></p>
          </div>
        </div>
      </div>
      <!-- Google Maps -->
      <div v-if="googleMapsEmbedUrl || googleMapsLinkUrl" class="public-home__maps-wrap">
        <div v-if="googleMapsEmbedUrl" class="public-home__maps-embed">
          <iframe
            :src="googleMapsEmbedUrl"
            width="100%"
            height="400"
            style="border:0;"
            allowfullscreen=""
            loading="lazy"
            referrerpolicy="no-referrer-when-downgrade"
            title="Localização no Google Maps"
          />
        </div>
        <a
          v-if="googleMapsLinkUrl && false"
          :href="googleMapsLinkUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="btn btn-secondary public-home__maps-link"
        >
          <i class="material-icons">map</i>
          Ver no Google Maps
        </a>
      </div>
    </section>

    <!-- Footer -->
    <footer class="public-home__footer">
      <p class="public-home__footer-text">
        &copy; {{ currentYear }} Rafaela Vieira Lashes. Todos os direitos reservados.
      </p>
    </footer>

    <!-- Sticky Booking CTA -->
    <div v-if="selectedServiceIds.size > 0 && step === 1" class="public-home__sticky-cta">
      <div class="public-home__sticky-cta-content">
        <span class="public-home__sticky-cta-text">
          {{ selectedServiceIds.size }} serviço{{ selectedServiceIds.size > 1 ? 's' : '' }} selecionado{{ selectedServiceIds.size > 1 ? 's' : '' }}
        </span>
        <button type="button" class="btn btn-primary" @click="scrollToBooking">
          Agendar
        </button>
      </div>
    </div>

    <!-- Floating WhatsApp Button -->
    <a
      :href="whatsappUrl"
      target="_blank"
      rel="noopener noreferrer"
      class="public-home__whatsapp-float"
      aria-label="Falar no WhatsApp"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
        class="public-home__whatsapp-icon"
        aria-hidden="true"
      >
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
      </svg>
    </a>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { firestore } from '@/firebase';
import PhoneInput from '@/components/PhoneInput.vue';
import { normalizeToRaw, formatPhoneForDisplay } from '@/utils/phoneUtils';
import { useGoogleReviewsStore } from '@/stores/googleReviews';
import { useAvailableSlotsStore } from '@/stores/availableSlots';
import { usePublicServiceStore } from '@/stores/publicService';

const SLOT_MS = 30 * 60 * 1000;

function toDateKey(ms) {
  const d = new Date(ms);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Compute available slots for the single selected service from per-user slots.
 * Returns array of { date, endDate, lash } with one provider per slot; if multiple providers
 * have the same start time, any one is attached.
 */
function computeAvailableStartTimes(userSlots, selectedServiceIds, publicServices) {
  const serviceId = selectedServiceIds && selectedServiceIds.size > 0 ? Array.from(selectedServiceIds)[0] : null;
  if (!serviceId) return [];
  const userIds = Object.keys(userSlots || {});
  if (userIds.length === 0) return [];

  const svc = publicServices.find((s) => s.id === serviceId);
  const slotsRequired = svc && Number(svc.slotsRequired) >= 1 ? Number(svc.slotsRequired) : 1;

  const candidates = [];
  for (const uid of userIds) {
    const entry = userSlots[uid];
    const serviceIds = entry && Array.isArray(entry.serviceIds) ? entry.serviceIds : [];
    if (!serviceIds.includes(serviceId)) continue;
    const slots = entry && Array.isArray(entry.slots) ? entry.slots : [];
    const startsSet = new Set(slots.map((s) => s.date));
    for (const slot of slots) {
      const startMs = slot.date;
      let hasConsecutive = true;
      for (let i = 0; i < slotsRequired; i++) {
        if (!startsSet.has(startMs + i * SLOT_MS)) {
          hasConsecutive = false;
          break;
        }
      }
      if (hasConsecutive) {
        const endDate = startMs + slotsRequired * SLOT_MS;
        candidates.push({ date: startMs, endDate, lash: uid });
      }
    }
  }

  const byDate = new Map();
  for (const c of candidates) {
    if (!byDate.has(c.date)) byDate.set(c.date, c);
  }
  const now = Date.now();
  const result = Array.from(byDate.values())
    .filter((s) => s.endDate > now)
    .sort((a, b) => a.date - b.date);
  return result;
}

export default {
  name: 'PublicHome',
  components: { PhoneInput },
  setup() {
    const step = ref(1);
    const selectedServiceIds = ref(new Set());
    const expandedGroups = ref(new Set());
    const expandedDescriptionIds = ref(new Set());
    const availableSlotsStore = useAvailableSlotsStore();
    const publicServiceStore = usePublicServiceStore();
    const publicServices = computed(() => publicServiceStore.list);
    const loading = computed(() => publicServiceStore.loading);
    const slotsByDate = computed(() => availableSlotsStore.slotsByDate);
    const slotsLoading = computed(() => availableSlotsStore.loading);
    const selectedSlot = ref(null);
    const selectedDate = ref('');
    const clientName = ref('');
    const clientPhone = ref('');
    const nameError = ref('');
    const phoneError = ref('');
    const submitLoading = ref(false);
    const contactInfo = {
      address: process.env.VUE_APP_ADDRESS || '',
      phone: formatPhoneForDisplay(process.env.VUE_APP_PHONE) || '',
      email: process.env.VUE_APP_EMAIL || '',
      businessHours: process.env.VUE_APP_BUSINESS_HOURS || '',
    };

    /** Converts \n or \\n in env strings to <br> for v-html (trusted: env only). */
    function newlinesToBr(str) {
      if (!str || typeof str !== 'string') return '';
      return str.replace(/\\n/g, '<br>').replace(/\n/g, '<br>');
    }

    const addressHtml = computed(() => newlinesToBr(contactInfo.address));
    const businessHoursHtml = computed(() => newlinesToBr(contactInfo.businessHours));

    const googleMapsEmbedUrl = process.env.VUE_APP_GOOGLE_MAPS_EMBED_URL || '';
    const googleMapsLinkUrl = process.env.VUE_APP_GOOGLE_MAPS_LINK || '';

    const reviewsStore = useGoogleReviewsStore();
    const googleReviews = computed(() => reviewsStore.data);
    const googleReviewsLoading = computed(() => reviewsStore.loading);
    const googleReviewsError = computed(() => reviewsStore.error);

    const reviewsCarouselRef = ref(null);
    const reviewsCarouselIndex = ref(0);

    const whatsappNumber = (process.env.VUE_APP_WHATSAPP_NUMBER || '').replace(/\D/g, '');
    const whatsappUrl = ref(
      whatsappNumber
        ? `https://wa.me/55${whatsappNumber.startsWith('55') ? whatsappNumber.slice(2) : whatsappNumber}`
        : 'https://wa.me/5511999999999'
    );

    const currentYear = new Date().getFullYear();

    function formatPrice(price) {
      return Number(price || 0).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      });
    }

    function scrollToBooking() {
      const bookingEl = document.getElementById('booking');
      if (bookingEl) {
        bookingEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        if (selectedServiceIds.value.size > 0 && step.value === 1) {
          setTimeout(() => goToStep(2), 300);
        }
      }
    }

    function toggleService(service) {
      if (selectedServiceIds.value.has(service.id)) {
        selectedServiceIds.value = new Set();
      } else {
        selectedServiceIds.value = new Set([service.id]);
      }
    }

    function toggleGroup(groupName) {
      const next = new Set(expandedGroups.value);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      expandedGroups.value = next;
    }

    function toggleDescription(serviceId) {
      const next = new Set(expandedDescriptionIds.value);
      if (next.has(serviceId)) {
        next.delete(serviceId);
      } else {
        next.add(serviceId);
      }
      expandedDescriptionIds.value = next;
    }

    function goToStep(s) {
      step.value = s;
      if (s === 2 && !selectedDate.value) {
        const today = new Date();
        selectedDate.value = today.toISOString().slice(0, 10);
      }
      const bookingEl = document.getElementById('booking');
      if (bookingEl) {
        bookingEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    const REVIEW_CARD_GAP = 0;

    const reviewsTrackStyle = computed(() => {
      const n = googleReviews.value?.reviews?.length ?? 0;
      if (n === 0) return {};
      return { width: `${n * 100}%` };
    });

    const reviewsCardWrapStyle = computed(() => {
      const n = googleReviews.value?.reviews?.length ?? 0;
      if (n === 0) return {};
      return { flexBasis: `${100 / n}%` };
    });

    function getReviewInitials(name) {
      if (!name || typeof name !== 'string') return '?';
      const parts = name.trim().split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase().slice(0, 2);
      }
      return (name[0] || '?').toUpperCase();
    }

    function getReviewCarouselStep() {
      const el = reviewsCarouselRef.value;
      if (!el) return 300;
      return el.clientWidth + REVIEW_CARD_GAP;
    }

    function goToReviewSlide(index) {
      const el = reviewsCarouselRef.value;
      if (!el || !googleReviews.value?.reviews?.length) return;
      const count = googleReviews.value.reviews.length;
      const i = Math.max(0, Math.min(index, count - 1));
      reviewsCarouselIndex.value = i;
      const step = getReviewCarouselStep();
      el.scrollTo({ left: i * step, behavior: 'smooth' });
    }

    function onReviewsScroll() {
      const el = reviewsCarouselRef.value;
      if (!el) return;
      const step = getReviewCarouselStep();
      const index = Math.round(el.scrollLeft / step);
      const maxIndex = (googleReviews.value?.reviews?.length ?? 1) - 1;
      reviewsCarouselIndex.value = Math.max(0, Math.min(index, maxIndex));
    }

    function getStarIcon(n) {
      const rating = googleReviews.value?.rating ?? 0;
      if (n <= Math.floor(rating)) return 'star';
      if (n - 0.5 <= rating) return 'star_half';
      return 'star_border';
    }

    const groupedServices = computed(() => {
      const groups = {};
      for (const service of publicServices.value) {
        const groupName = service.group && service.group.trim() ? service.group.trim() : 'Outros';
        if (!groups[groupName]) {
          groups[groupName] = [];
        }
        groups[groupName].push(service);
      }
      for (const groupName in groups) {
        groups[groupName].sort((a, b) => {
          const orderA = a.order ?? 999;
          const orderB = b.order ?? 999;
          if (orderA !== orderB) return orderA - orderB;
          return (a.name || '').localeCompare(b.name || '');
        });
      }
      const sortedGroups = Object.keys(groups).sort((a, b) => {
        if (a === 'Outros') return 1;
        if (b === 'Outros') return -1;
        return groups[a][0].order - groups[b][0].order;
      });
      const result = {};
      for (const groupName of sortedGroups) {
        result[groupName] = groups[groupName];
      }
      return result;
    });

    const selectedServicesList = computed(() => {
      return publicServices.value.filter((s) => selectedServiceIds.value.has(s.id));
    });

    const selectedService = computed(() => {
      if (selectedServicesList.value.length === 0) return null;
      return selectedServicesList.value[0];
    });

    const filteredSlotsByDate = computed(() => {
      if (!selectedDate.value) return {};
      const userSlots = availableSlotsStore.userSlotsForDate(selectedDate.value);
      const daySlots = computeAvailableStartTimes(
        userSlots,
        selectedServiceIds.value,
        publicServices.value
      );
      return { [selectedDate.value]: daySlots };
    });

    const minDate = computed(() => {
      const today = new Date();
      return today.toISOString().slice(0, 10);
    });

    const maxDate = computed(() => {
      const max = new Date();
      max.setDate(max.getDate() + 60);
      return max.toISOString().slice(0, 10);
    });

    watch(selectedDate, (newDate) => {
      if (newDate && selectedSlot.value) {
        const slotDateKey = toDateKey(selectedSlot.value.date);
        if (slotDateKey !== newDate) {
          selectedSlot.value = null;
        }
      }
    });

    watch(selectedServiceIds, () => {
      step.value = 1;
      selectedDate.value = '';
      selectedSlot.value = null;
    }, { deep: true });

    function selectSlot(slot) {
      selectedSlot.value = slot;
    }

    function formatSlotTime(ms) {
      const d = new Date(ms);
      return d.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
      });
    }

    function formatDayLabel(dateKey) {
      const [y, m, day] = dateKey.split('-').map(Number);
      const d = new Date(y, m - 1, day);
      const today = new Date();
      if (d.toDateString() === today.toDateString()) return 'Hoje';
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      if (d.toDateString() === tomorrow.toDateString()) return 'Amanhã';
      return d.toLocaleDateString('pt-BR', { weekday: 'short', day: 'numeric', month: 'short' });
    }

    function validateForm() {
      nameError.value = '';
      phoneError.value = '';
      let ok = true;
      if (!clientName.value.trim()) {
        nameError.value = 'Informe o seu nome.';
        ok = false;
      }
      const phoneRaw = normalizeToRaw(clientPhone.value) || (clientPhone.value || '').trim();
      if (!phoneRaw) {
        phoneError.value = 'Informe o seu telefone.';
        ok = false;
      }
      return ok;
    }

    async function submit() {
      if (!validateForm() || !selectedSlot.value) return;
      submitLoading.value = true;
      try {
        const phoneRaw = normalizeToRaw(clientPhone.value) || clientPhone.value.trim();
        const selectedServices = publicServices.value
          .filter((s) => selectedServiceIds.value.has(s.id))
          .map((s) => ({ serviceId: s.id, price: Number(s.price) || 0 }));
        const payload = {
          clientName: clientName.value.trim(),
          clientPhone: phoneRaw,
          date: selectedSlot.value.date,
          endDate: selectedSlot.value.endDate,
          services: selectedServices,
          createdAt: serverTimestamp(),
        };
        if (selectedSlot.value.lash) payload.lash = selectedSlot.value.lash;
        await addDoc(collection(firestore, 'appointmentRequests'), payload);
        step.value = 'success';
        const bookingEl = document.getElementById('booking');
        if (bookingEl) {
          bookingEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } catch (err) {
        console.warn('Submit failed', err);
        phoneError.value = 'Não foi possível enviar. Tente novamente.';
      } finally {
        submitLoading.value = false;
      }
    }

    function resetBooking() {
      step.value = 1;
      selectedServiceIds.value = new Set();
      selectedSlot.value = null;
      clientName.value = '';
      clientPhone.value = '';
      nameError.value = '';
      phoneError.value = '';
      scrollToBooking();
    }

    /* function openWhatsApp() {
      const phone = whatsappNumber;
      const encoded = encodeURIComponent("Olá! Gostaria de agendar um horário.");

      const isAndroidDevice = /Android/i.test(navigator.userAgent);
      const isStandalone =
        window.matchMedia('(display-mode: standalone)').matches;

      const intentUrl =
        `intent://send?phone=${phone}&text=${encoded}` +
        `#Intent;scheme=whatsapp;package=com.whatsapp;end`;

      const webUrl = `https://wa.me/${phone}?text=${encoded}`;

      if (isAndroidDevice && isStandalone) {
        // Best native experience for Android PWA
        window.location.href = intentUrl;

        // fallback if WhatsApp not installed
        setTimeout(() => {
          window.open(webUrl, '_blank');
        }, 700);

      } else if (isAndroidDevice) {
        // Android browser
        window.open(intentUrl, '_blank');

        setTimeout(() => {
          window.open(webUrl, '_blank');
        }, 700);

      } else {
        // iOS and desktop
        window.open(webUrl, '_blank');
      }
    } */

    /*watch(publicServices, (list) => {
      if (list.length > 0 && expandedGroups.value.size === 0) {
        const firstGroup = list[0].group && list[0].group.trim() ? list[0].group.trim() : 'Outros';
        expandedGroups.value = new Set([...expandedGroups.value, firstGroup]);
      }
    }, { immediate: true });*/

    onMounted(() => {
      publicServiceStore.subscribe();
      reviewsStore.subscribe();
    });

    return {
      publicServices,
      groupedServices,
      selectedServicesList,
      selectedService,
      expandedGroups,
      expandedDescriptionIds,
      toggleDescription,
      loading,
      whatsappUrl,
      formatPrice,
      step,
      selectedServiceIds,
      slotsLoading,
      slotsByDate,
      filteredSlotsByDate,
      selectedSlot,
      selectedDate,
      minDate,
      maxDate,
      clientName,
      clientPhone,
      nameError,
      phoneError,
      submitLoading,
      contactInfo,
      addressHtml,
      businessHoursHtml,
      googleMapsEmbedUrl,
      googleMapsLinkUrl,
      googleReviews,
      googleReviewsLoading,
      googleReviewsError,
      reviewsCarouselRef,
      reviewsCarouselIndex,
      goToReviewSlide,
      onReviewsScroll,
      getStarIcon,
      getReviewInitials,
      reviewsTrackStyle,
      reviewsCardWrapStyle,
      currentYear,
      scrollToBooking,
      toggleService,
      toggleGroup,
      goToStep,
      selectSlot,
      formatSlotTime,
      formatDayLabel,
      toDateKey,
      submit,
      resetBooking,
    };
  },
};
</script>

<style scoped>
.public-home {
  padding: var(--space-lg) 0;
  padding-bottom: 0;
}

.public-home__section {
  border-bottom: 1px solid var(--color-border);
}

.public-home__section:last-of-type {
  border-bottom: none;
  padding-bottom: 0px;
}

.public-home__hero {
  text-align: center;
  margin-bottom: 0;
}

.public-home__hero-photo-wrap {
  margin: 0 auto var(--space-lg);
  width: 200px;
  height: 200px;
  border-radius: 50%;
  overflow: hidden;
  border: 4px solid var(--color-accent-soft);
  box-shadow: var(--shadow-md);
  background: var(--color-bg);
}

.public-home__hero-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.public-home__hero-title {
  font-size: 2rem;
  font-weight: 600;
  color: var(--color-text);
  margin: 0 0 var(--space-sm);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.public-home__hero-subtitle {
  font-size: 1.1rem;
  color: var(--color-text-secondary);
  margin: 0;
}

.public-home__cta-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-md);
  justify-content: center;
  align-items: center;
}

.public-home__cta-wrap--bottom {
  margin-top: var(--space-lg);
}

.public-home__cta-wrap--contact {
  margin-top: var(--space-lg);
}

.public-home__cta {
  display: inline-flex;
  align-items: center;
  gap: var(--space-sm);
  text-decoration: none;
}

.public-home__section-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--color-text);
  margin: 0 0 var(--space-xs);
  text-transform: uppercase;
  letter-spacing: 0.02em;
  text-align: center;
}

.public-home__section-subtitle {
  font-size: 0.95rem;
  color: var(--color-text-secondary);
  margin: 0 0 var(--space-xl);
  text-align: center;
}

.public-home__section {
  padding: var(--space-xl) 0;
}

.public-home__loading {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  color: var(--color-text-secondary);
  padding: var(--space-xl);
  justify-content: center;
}

.public-home__loading .material-icons.spin {
  animation: auth-loading-spin 1s linear infinite;
}

/* Google Reviews */
.public-home__reviews-error {
  text-align: center;
  padding: var(--space-xl);
  color: var(--color-text-secondary);
}

.public-home__reviews-error .btn {
  margin-top: var(--space-md);
}

.public-home__reviews-content {
  max-width: 56rem;
  margin: 0 auto;
}

/* Google Rating card: green top border, rounded, logo left / content right */
.public-home__reviews-header-card {
  border-radius: var(--radius-md, 8px);
  background: #fff;
  border: 1px solid var(--color-border);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  border-top: 3px solid var(--color-accent);
  margin-bottom: var(--space-md);
  overflow: hidden;
}

.public-home__reviews-header-card-inner {
  display: flex;
  align-items: center;
  gap: var(--space-lg);
  padding: var(--space-lg);
}

.public-home__reviews-google-logo {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
}

.public-home__reviews-google-logo svg {
  width: 100%;
  height: 100%;
  display: block;
}

.public-home__reviews-header-content {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
}

.public-home__reviews-card-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-text);
  margin: 0;
}

.public-home__reviews-summary {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  flex-wrap: wrap;
}

.public-home__reviews-rating-num {
  font-size: 1.75rem;
  font-weight: 700;
  color: #f9ab00;
}

.public-home__reviews-stars {
  display: flex;
  align-items: center;
  gap: 2px;
}

.public-home__reviews-stars .material-icons {
  font-size: 1.5rem;
  color: #f9ab00;
}

.public-home__reviews-count {
  margin: 0;
  font-size: 0.875rem;
  color: var(--color-text-secondary);
}

.public-home__reviews-count-link {
  color: var(--color-text-secondary);
  /*text-decoration: none;*/
}

.public-home__reviews-count-link:hover {
  text-decoration: underline;
  color: var(--color-text);
}

.public-home__reviews-cta-btn {
  display: block;
  margin-bottom: var(--space-xl);
  width: 100%;
  max-width: 200px;
  margin-left: 0;
  margin-right: auto;
}

/* Carousel wrap: scroll area with overlay arrows */
.public-home__reviews-carousel-wrap {
  position: relative;
  margin-bottom: var(--space-lg);
}

.public-home__reviews-carousel {
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.public-home__reviews-carousel::-webkit-scrollbar {
  display: none;
}

.public-home__reviews-track {
  display: flex;
  gap: 12px;
  /*gap: 0;*/
  padding: var(--space-sm) 0;
  min-height: 0;
}

.public-home__review-card-wrap {
  flex: 0 0 100%;
  min-width: 0;
  scroll-snap-align: start;
  box-sizing: border-box;
}

.public-home__reviews-carousel-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid var(--color-border);
  background: var(--color-bg);
  color: var(--color-text);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, border-color 0.2s;
  z-index: 1;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

.public-home__reviews-carousel-btn:hover:not(:disabled) {
  background: var(--color-bg-soft, #f5f5f5);
  border-color: var(--color-text-secondary);
}

.public-home__reviews-carousel-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.public-home__reviews-carousel-btn .material-icons {
  font-size: 1.25rem;
}

.public-home__reviews-carousel-btn--prev {
  left: -15px;
}

.public-home__reviews-carousel-btn--next {
  right: -15px;
}

/* Review card */
.public-home__review-card {
  background: #fff;
  border-radius: var(--radius-md, 8px);
  padding: var(--space-md);
  border: 1px solid var(--color-border);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.public-home__review-header {
  display: flex;
  align-items: center;
  gap: var(--space-md);
  margin-bottom: var(--space-md);
}

.public-home__review-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--color-accent);
  color: #fff;
  font-size: 0.875rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.public-home__review-meta {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.public-home__review-author {
  font-weight: 700;
  font-size: 0.9375rem;
  color: var(--color-text);
}

.public-home__review-time {
  font-size: 0.8125rem;
  color: var(--color-text-secondary);
}

.public-home__review-text {
  margin: 0 0 var(--space-md);
  font-size: 0.875rem;
  line-height: 1.5;
  color: var(--color-text);
  display: block;
}

.public-home__review-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-sm);
  margin-top: auto;
  padding-top: var(--space-sm);
  border-top: 1px solid var(--color-border);
}

.public-home__review-stars {
  display: flex;
  align-items: center;
  gap: 2px;
}

.public-home__review-stars .material-icons {
  font-size: 1.125rem;
  color: #f9ab00;
}

.public-home__review-google-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.public-home__review-google-icon svg {
  width: 100%;
  height: 100%;
  display: block;
}

/* Pagination dots */
.public-home__reviews-dots {
  display: flex;
  justify-content: center;
  gap: var(--space-xs);
  flex-wrap: wrap;
}

.public-home__reviews-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  background: var(--color-border);
  cursor: pointer;
  padding: 0;
  transition: background 0.2s, transform 0.2s;
}

.public-home__reviews-dot:hover {
  background: var(--color-text-secondary);
}

.public-home__reviews-dot--active {
  background: var(--color-text);
  transform: scale(1.2);
}

.public-home__reviews-fallback {
  text-align: center;
  padding: var(--space-xl);
}

.public-home__empty {
  text-align: center;
  color: var(--color-text-secondary);
}

/* Selected Services Summary */
.public-home__selected-summary {
  background: var(--color-accent-soft);
  border: 2px solid var(--color-accent);
  border-radius: var(--radius-md);
  padding: var(--space-md);
  margin-bottom: var(--space-xl);
}

.public-home__selected-summary-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-md);
  flex-wrap: wrap;
}

.public-home__selected-summary-info {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  color: var(--color-text);
  font-weight: 500;
}

.public-home__selected-summary-info .material-icons {
  color: var(--color-accent);
  font-size: 20px;
}

.public-home__selected-count {
  font-size: 0.95rem;
}

/* Service Groups */
.public-home__section--services .public-home__section-subtitle {
  margin-bottom: var(--space-xl);
}

.public-home__service-groups {
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
}

.public-home__service-group {
  background: var(--color-bg);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  overflow: hidden;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.public-home__service-group-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-text);
  margin: 0;
  padding: var(--space-md) var(--space-lg);
  display: flex;
  align-items: center;
  gap: var(--space-md);
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  font-family: inherit;
}

.public-home__service-group-title:hover {
  background: var(--color-bg-soft);
}

.public-home__service-group-title--expanded {
  border-bottom: 1px solid var(--color-border);
}

.public-home__service-group-chevron {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: var(--radius-sm);
  background: var(--color-accent-soft);
  color: var(--color-accent);
  flex-shrink: 0;
}

.public-home__service-group-chevron .material-icons {
  font-size: 24px;
}

.public-home__service-group-name {
  flex: 1;
  letter-spacing: 0.01em;
}

.public-home__service-group-count {
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--color-text-secondary);
  background: var(--color-bg-soft);
  padding: 4px 10px;
  border-radius: 999px;
  min-width: 28px;
  text-align: center;
}

.public-home__service-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: var(--space-lg);
  padding: var(--space-lg);
}

.public-home__service-card {
  background: var(--color-surface);
  border-radius: var(--radius-md);
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid var(--color-border);
  display: flex;
  flex-direction: column;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;
  position: relative;
}

.public-home__service-card:hover {
  border-color: var(--color-accent);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.public-home__service-card--selected {
  border-color: var(--color-accent);
  border-width: 2px;
  background: var(--color-surface);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

.public-home__service-image-wrap {
  width: 100%;
  height: 150px;
  background: var(--color-bg);
  overflow: hidden;
}

.public-home__service-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.3s ease;
}

.public-home__service-card:hover .public-home__service-image {
  transform: scale(1.05);
}

.public-home__service-image-placeholder {
  width: 100%;
  height: 120px;
  background: linear-gradient(135deg, var(--color-accent-soft) 0%, var(--color-bg) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-accent);
}

.public-home__service-image-placeholder .material-icons {
  font-size: 48px;
  opacity: 0.7;
}

.public-home__service-body {
  padding: var(--space-md) var(--space-lg);
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
  flex: 1;
  min-height: 0;
}

.public-home__service-name {
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-text);
  margin: 0;
  line-height: 1.35;
}

.public-home__service-more {
  display: inline-block;
  margin: 0;
  padding: 0;
  font-size: 0.8125rem;
  font-family: inherit;
  font-weight: 500;
  color: var(--color-accent);
  background: none;
  border: none;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
  transition: color 0.2s;
}

.public-home__service-more:hover {
  color: var(--color-accent-hover);
}

.public-home__service-desc {
  font-size: 0.8125rem;
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.public-home__service-desc--expanded {
  display: block;
  -webkit-line-clamp: unset;
  line-clamp: unset;
  overflow: visible;
}

.public-home__service-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
  padding-top: var(--space-md);
  border-top: 1px solid var(--color-border);
}

.public-home__service-price {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--color-accent);
  margin: 0;
}

.public-home__service-selected-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--color-accent);
  color: #fff;
  flex-shrink: 0;
}

.public-home__service-selected-badge .material-icons {
  font-size: 20px;
}

/* Selected Services Chips (Step 1) */
.public-home__booking .public-home__selected-services-chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-sm);
}

.public-home__service-chip {
  display: inline-flex;
  align-items: center;
  gap: var(--space-sm);
  background: var(--color-bg);
  border: 1px solid var(--color-accent);
  border-radius: var(--radius-md);
  padding: var(--space-sm) var(--space-md);
  font-size: 0.9375rem;
  color: var(--color-text);
  transition: box-shadow 0.2s;
}

.public-home__service-chip:hover {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
}

.public-home__service-chip-name {
  font-weight: 500;
}

.public-home__service-chip-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: transparent;
  border: none;
  color: var(--color-text-secondary);
  cursor: pointer;
  padding: 0;
  transition: background 0.2s, color 0.2s;
}

.public-home__service-chip-remove:hover {
  background: var(--color-accent-soft);
  color: var(--color-accent);
}

.public-home__service-chip-remove .material-icons {
  font-size: 18px;
}

/* Booking Section */
.public-home__booking {
  background: var(--color-surface);
  padding: var(--space-xl) var(--space-md);
}

.public-home__step-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--space-xl);
  max-width: 420px;
  margin-left: auto;
  margin-right: auto;
}

.public-home__step-indicator-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-sm);
  flex-shrink: 0;
  transition: opacity 0.2s;
}

.public-home__step-indicator-item:not(.active):not(.completed) .public-home__step-label {
  color: var(--color-text-muted);
}

.public-home__step-connector {
  flex: 1;
  height: 2px;
  min-width: 24px;
  max-width: 48px;
  background: var(--color-border);
  margin-bottom: 22px;
}

.public-home__step-indicator-item.completed + .public-home__step-connector {
  background: var(--color-accent);
  opacity: 0.5;
}

.public-home__step-number {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--color-bg);
  border: 2px solid var(--color-border);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.9375rem;
  color: var(--color-text-secondary);
  transition: border-color 0.2s, background 0.2s, color 0.2s;
}

.public-home__step-indicator-item.active .public-home__step-number {
  background: var(--color-accent);
  border-color: var(--color-accent);
  color: #fff;
}

.public-home__step-indicator-item.completed .public-home__step-number {
  background: var(--color-accent);
  border-color: var(--color-accent);
  color: #fff;
}

.public-home__step-indicator-item.completed .public-home__step-number-inner {
  display: none;
}

.public-home__step-indicator-item.completed .public-home__step-number::after {
  content: '✓';
  font-size: 1.125rem;
  font-weight: 700;
  line-height: 1;
}

.public-home__step-label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--color-text);
  letter-spacing: 0.02em;
}

.public-home__booking-step {
  max-width: 520px;
  margin: 0 auto;
}

.public-home__step-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 var(--space-sm);
  color: var(--color-text);
}

.public-home__step-desc {
  font-size: 0.9375rem;
  color: var(--color-text-secondary);
  margin: 0 0 var(--space-lg);
  line-height: 1.45;
}

.public-home__hint {
  font-size: 0.9375rem;
  color: var(--color-text-secondary);
  margin: 0 0 var(--space-lg);
  padding: var(--space-md) var(--space-lg);
  background: var(--color-bg);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  display: flex;
  align-items: center;
  gap: var(--space-sm);
}

.public-home__hint .material-icons {
  font-size: 1.25rem;
  color: var(--color-accent);
  flex-shrink: 0;
}

.public-home__selected-services-chips {
  margin-bottom: var(--space-lg);
}

.public-home__date-picker-wrap {
  margin-bottom: var(--space-lg);
}

.public-home__date-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: var(--space-sm);
  color: var(--color-text);
}

.public-home__date-input {
  width: 100%;
  padding: var(--space-sm) var(--space-md);
  font-size: 1rem;
  font-family: inherit;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  color: var(--color-text);
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.public-home__date-input:hover {
  border-color: var(--color-text-secondary);
}

.public-home__date-input:focus {
  outline: none;
  border-color: var(--color-accent);
  box-shadow: 0 0 0 3px var(--color-accent-soft);
}

.public-home__slots-wrap {
  margin-bottom: var(--space-lg);
}

.public-home__day-group {
  margin-bottom: var(--space-md);
}

.public-home__slot-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(60px, 1fr));
  gap: var(--space-sm);
}

.public-home__slot-btn {
  padding: var(--space-sm);
  font-size: 0.9375rem;
  font-family: inherit;
  font-weight: 500;
  background: var(--color-bg);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  cursor: pointer;
  color: var(--color-text);
  transition: border-color 0.2s, background 0.2s, color 0.2s;
}

.public-home__slot-btn:hover:not(.selected) {
  border-color: var(--color-accent);
  background: var(--color-accent-soft);
}

.public-home__slot-btn.selected {
  border-color: var(--color-accent);
  background: var(--color-accent);
  color: #fff;
}

.public-home__booking-actions {
  display: flex;
  gap: var(--space-md);
  margin-top: var(--space-xl);
  justify-content: stretch;
  flex-wrap: wrap;
}

.public-home__booking-actions .btn {
  flex: 1;
  min-width: 120px;
  font-weight: 600;
  border-radius: var(--radius-md);
}

.public-home__selected-service-summary {
  display: flex;
  align-items: center;
  gap: var(--space-xs);
  margin-bottom: var(--space-lg);
  padding: var(--space-md);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  font-size: 0.9375rem;
  flex-wrap: wrap;
}

.public-home__selected-service-label {
  font-weight: 600;
  color: var(--color-text-secondary);
}

.public-home__selected-service-name {
  font-weight: 500;
  color: var(--color-text);
}

.public-home__selected-service-price {
  margin-left: auto;
  font-weight: 600;
  color: var(--color-accent);
}

.public-home__booking-summary {
  margin-bottom: var(--space-lg);
  padding: var(--space-md);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
}

.public-home__summary-item {
  display: flex;
  align-items: center;
  gap: var(--space-xs);
  margin-bottom: var(--space-sm);
  font-size: 0.9375rem;
  flex-wrap: wrap;
}

.public-home__summary-item:last-child {
  margin-bottom: 0;
}

.public-home__summary-label {
  font-weight: 600;
  color: var(--color-text-secondary);
}

.public-home__summary-value {
  font-weight: 500;
  color: var(--color-text);
  flex: 1;
}

.public-home__summary-price {
  margin-left: auto;
  font-weight: 600;
  color: var(--color-accent);
}

.public-home__booking-form .form-group {
  margin-bottom: var(--space-lg);
}

.public-home__booking-form label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: var(--space-sm);
  color: var(--color-text);
}

.public-home__booking-form input[type="text"],
.public-home__booking-form input[type="tel"] {
  width: 100%;
  font-size: 1rem;
  font-family: inherit;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  color: var(--color-text);
  transition: border-color 0.2s, box-shadow 0.2s;
}

.public-home__booking-form input:focus {
  outline: none;
  border-color: var(--color-accent);
  box-shadow: 0 0 0 3px var(--color-accent-soft);
}

.public-home__booking-form .public-home__booking-actions {
  margin-top: var(--space-xl);
}

.public-home__booking-form {
  max-width: 100%;
}

.public-home__booking-success {
  text-align: center;
  padding: var(--space-xl) 0;
}

.public-home__success-icon {
  font-size: 72px;
  color: var(--color-accent);
  margin-bottom: var(--space-md);
}

.public-home__success-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 var(--space-sm);
  color: var(--color-text);
}

.public-home__success-text {
  color: var(--color-text-secondary);
  margin: 0 0 var(--space-lg);
  font-size: 1rem;
  line-height: 1.5;
}

/* About Section */
.public-home__about-content {
  max-width: 600px;
  margin: 0 auto;
}

.public-home__about-text {
  font-size: 1rem;
  line-height: 1.6;
  color: var(--color-text-secondary);
  text-align: center;
  margin: 0;
}

/* Contact Section */
.public-home__contact {
  background: var(--color-surface);
}

.public-home__contact-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: var(--space-xl);
  margin-bottom: var(--space-xl);
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
}

.public-home__contact-item {
  display: flex;
  gap: var(--space-md);
  align-items: flex-start;
}

.public-home__contact-icon {
  font-size: 26px;
  color: var(--color-accent);
  flex-shrink: 0;
  margin-top: 0px;
  margin-left: 20px;
}

.public-home__contact-info {
  flex: 1;
}

.public-home__contact-label {
  font-size: 0.9rem;
  font-weight: 600;
  margin: 0 0 var(--space-xs);
  color: var(--color-text);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.public-home__contact-value {
  font-size: 0.9375rem;
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.6;
}

.public-home__contact-link {
  color: var(--color-accent);
  text-decoration: none;
  transition: color 0.2s;
  font-weight: 500;
}

.public-home__contact-link:hover {
  color: var(--color-accent-hover);
  text-decoration: underline;
}

.public-home__maps-wrap {
  margin-top: var(--space-xl);
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
}

.public-home__maps-embed {
  overflow: hidden;
}

.public-home__maps-embed iframe {
  display: block;
  width: 100%;
}

.public-home__maps-link {
  display: inline-flex;
  align-items: center;
  gap: var(--space-sm);
  text-decoration: none;
  margin-top: var(--space-md);
}

/* Footer */
.public-home__footer {
  padding: var(--space-lg) 0;
  text-align: center;
  background: var(--color-bg);
  border-top: 1px solid var(--color-border);
}

.public-home__footer-text {
  font-size: 0.875rem;
  color: var(--color-text-muted);
  margin: 0;
  padding: 0 var(--space-md);
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.field-error {
  font-size: 0.875rem;
  color: var(--color-error);
  margin-top: var(--space-xs);
  margin-bottom: 0;
}

/* Sticky Booking CTA */
.public-home__sticky-cta {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 999;
  background: var(--color-surface);
  border-top: 2px solid var(--color-accent);
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1);
  padding: var(--space-md);
  display: none;
}

.public-home__sticky-cta-content {
  max-width: 960px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-md);
  padding: 0 var(--space-md);
  padding-right: calc(var(--space-md) + 60px + var(--space-lg));
}

.public-home__sticky-cta-text {
  font-weight: 500;
  color: var(--color-text);
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Responsive Design */
@media (max-width: 768px) {
  .public-home__hero-photo-wrap {
    width: 160px;
    height: 160px;
    margin-bottom: var(--space-md);
  }

  .public-home__hero-title {
    font-size: 1.5rem;
  }

  .public-home__sticky-cta {
    display: block;
  }

  .public-home__service-grid {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: var(--space-lg);
  }

  .public-home__service-body {
    padding: var(--space-xs) var(--space-sm);
  }

  .public-home__service-name {
    font-size: 0.875rem;
  }

  .public-home__service-desc {
    font-size: 0.75rem;
    -webkit-line-clamp: 1;
    line-clamp: 1;
  }

  .public-home__service-price {
    font-size: 0.9rem;
  }

  .public-home__selected-summary-content {
    flex-direction: column;
    align-items: stretch;
  }

  .public-home__sticky-cta-content {
    flex-direction: column;
    align-items: stretch;
  }

  .public-home__sticky-cta-content .btn {
    width: 100%;
  }

  .public-home__service-group-title {
    font-size: 1rem;
    padding: var(--space-xs) var(--space-sm);
  }

  .public-home__section-title {
    font-size: 1.25rem;
  }

  .public-home__service-groups {
    gap: var(--space-sm);
  }
}

@media (min-width: 769px) and (max-width: 1024px) {
  .public-home__service-grid {
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  }

}

@media (min-width: 1025px) {
  .public-home__service-grid {
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  }
}

/* Floating WhatsApp Button */
.public-home__whatsapp-float {
  position: fixed;
  bottom: var(--space-lg);
  right: var(--space-lg);
  width: 60px;
  height: 60px;
  background: #25d366;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(37, 211, 102, 0.4);
  z-index: 1000;
  transition: all 0.3s ease;
  text-decoration: none;
  color: #fff;
}

.public-home__whatsapp-float:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 16px rgba(37, 211, 102, 0.5);
  background: #20ba5a;
}

.public-home__whatsapp-float:active {
  transform: scale(0.95);
}

.public-home__whatsapp-icon {
  width: 32px;
  height: 32px;
  fill: currentColor;
}

@media (max-width: 768px) {
  .public-home__whatsapp-float {
    width: 56px;
    height: 56px;
    bottom: var(--space-md);
    right: var(--space-md);
  }

  .public-home__whatsapp-icon {
    width: 28px;
    height: 28px;
  }

  .public-home__sticky-cta-content {
    padding-right: calc(var(--space-md) + 56px + var(--space-md));
  }
}
</style>
